import logo from "@/assets/logo.png";
import {type NavEntry, getGroupedEntries} from "@/navigation/nav-config";
import {NavLink} from "react-router";

const groups = getGroupedEntries();

/** Separate "system" entries (rendered at bottom) from the rest */
const mainGroups = groups.filter(g => g.key !== "system");
const systemItems = groups.find(g => g.key === "system")?.items ?? [];

interface SidebarProps {
  collapsed: boolean;
}

const SidebarLink = ({item, collapsed}: {item: NavEntry; collapsed: boolean}) => (
  <NavLink
    to={item.path}
    title={collapsed ? item.label : undefined}
    className={({isActive}) =>
      [
        "flex items-center gap-3 px-3 py-2.5 rounded-md mb-0.5 text-sm transition-all duration-(--transition-fast)",
        isActive
          ? "bg-(--color-sidebar-active) text-white font-medium"
          : "text-white/70 hover:bg-(--color-sidebar-hover) hover:text-white",
      ].join(" ")
    }
  >
    <span className="text-base shrink-0 w-5 text-center leading-none">{item.icon}</span>
    {!collapsed && <span className="truncate">{item.label}</span>}
  </NavLink>
);

export const Sidebar = ({collapsed}: SidebarProps) => (
  <aside
    className="flex flex-col h-full overflow-hidden transition-all duration-(--transition-normal)"
    style={{
      width: collapsed ? "var(--sidebar-collapsed-width)" : "var(--sidebar-width)",
      backgroundColor: "var(--color-sidebar-bg)",
    }}
  >
    {/* Logo */}
    <div
      className="flex items-center gap-3 px-4 border-b border-white/10"
      style={{height: "var(--header-height)"}}
    >
      <img
        src={logo}
        alt="R365"
        className="w-8 h-8 object-contain shrink-0 rounded-md"
      />
      {!collapsed && (
        <div>
          <p className="text-white text-sm font-bold leading-tight">Remesas 365</p>
          <p className="text-white/50 text-xs">Backoffice</p>
        </div>
      )}
    </div>

    {/* Navigation — grouped by service */}
    <nav className="flex-1 overflow-y-auto py-3 px-2">
      {mainGroups.map(group => (
        <div
          key={group.key}
          className="mb-3"
        >
          {!collapsed && (
            <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-white/40">{group.label}</p>
          )}
          {group.items.map(item => (
            <SidebarLink
              key={item.path}
              item={item}
              collapsed={collapsed}
            />
          ))}
        </div>
      ))}
    </nav>

    {/* Bottom — System entries */}
    {systemItems.length > 0 && (
      <div className="px-2 pb-4 border-t border-white/10 pt-4">
        {systemItems.map(item => (
          <SidebarLink
            key={item.path}
            item={item}
            collapsed={collapsed}
          />
        ))}
      </div>
    )}
  </aside>
);
